/**
 * Loading Screen — monochrome flow field, Xerox threshold.
 *
 * A low-res particle flow field morphs organically.
 * The accumulated field is thresholded: bright → white, dark → black.
 * Upscaled to fullscreen → organic edges, scale ambiguity.
 * That IS the Xerox — a copy of something that doesn't exist.
 *
 * The banker sees an instrument — precise, alive, beautiful.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { BootState, STATIC_FACTS, checkHealth, getAllFacts } from "../lib/boot-state";
import { bridgeSession, requestMagicLink, verifyMagicLink, getJWT } from "../lib/portal-session";

interface LoadingScreenProps {
  onReady: (jwt: string, tenantId: string | null, role: "client" | "principal") => void;
}

export function LoadingScreen({ onReady }: LoadingScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [bootState, setBootState] = useState<BootState>("checking");
  const [health, setHealth] = useState<any>(null);
  const [facts, setFacts] = useState<string[]>(STATIC_FACTS);
  const [factIndex, setFactIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const typingEnergyRef = useRef(0);

  // ── Flow field canvas ──────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current!;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    if (!ctx) return;

    let raf = 0;
    let time = 0;

    // ── Low-res flow field canvas ───────────────────────────────────────
    const FW = 320;  // field width (low-res)
    const FH = 213;  // field height (16:10 aspect)
    const off = document.createElement("canvas");
    off.width = FW;
    off.height = FH;
    const octx = off.getContext("2d")!;
    const imgData = octx.createImageData(FW, FH);
    const data = imgData.data;

    // ── Flow field particles ────────────────────────────────────────────
    const NP = 280; // particle count
    const px = new Float32Array(NP);
    const py = new Float32Array(NP);
    const pvx = new Float32Array(NP);
    const pvy = new Float32Array(NP);
    const plife = new Float32Array(NP);

    for (let i = 0; i < NP; i++) {
      px[i] = Math.random() * FW;
      py[i] = Math.random() * FH;
      pvx[i] = 0;
      pvy[i] = 0;
      plife[i] = Math.random() * 100;
    }

    // ── Field buffer — accumulates particle trails ─────────────────────
    const field = new Float32Array(FW * FH);
    // Decay: how fast trails fade. Lower = longer trails = more organic
    const DECAY = 0.91;

    // ── Noise function — multi-octave sine ──────────────────────────────
    function flowAngle(x: number, y: number, t: number): number {
      // Layer 1: slow large-scale flow
      let a = Math.sin(x * 0.012 + t * 0.3) * Math.cos(y * 0.010 + t * 0.2);
      // Layer 2: medium-scale turbulence
      a += Math.sin(x * 0.03 + y * 0.02 + t * 0.5) * 0.6;
      // Layer 3: fine detail
      a += Math.sin(x * 0.08 + t * 0.7) * Math.cos(y * 0.06 + t * 0.4) * 0.3;
      // Mouse disturbance — creates a local vortex
      if (mouseRef.current.active) {
        const mx = mouseRef.current.x * FW;
        const my = mouseRef.current.y * FH;
        const dx = x - mx;
        const dy = y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 100) {
          const influence = (1 - dist / 100) * 1.1;
          // Tangential component — gentle swirl
          a += (-dy / (dist + 1)) * influence * 1.2;
          a += Math.PI * 0.5 * influence;
        }
      }
      // Typing energy — injects turbulence
      const e = typingEnergyRef.current;
      if (e > 0.01) {
        a += Math.sin(x * 0.04 + y * 0.04 + t * 2.5) * e * 2.5;
      }
      return a * Math.PI;
    }

    // ── Mouse tracking ──────────────────────────────────────────────────
    function onMouseMove(e: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width;
      mouseRef.current.y = (e.clientY - rect.top) / rect.height;
      mouseRef.current.active = true;
    }
    function onMouseLeave() {
      mouseRef.current.active = false;
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseout", onMouseLeave);

    // ── Keystroke energy ─────────────────────────────────────────────────
    (window as any).__spawnKeyArt = () => {
      typingEnergyRef.current = Math.min(1.2, typingEnergyRef.current + 0.22);
    };

    // ── Animation loop ──────────────────────────────────────────────────
    function animate() {
      time += 0.0035;

      // Decay typing energy
      typingEnergyRef.current *= 0.93;
      // Illuminate the auth card based on typing energy — subtle dark glow on white
      const authCard = document.querySelector(".auth-card") as HTMLElement | null;
      if (authCard) {
        const e = typingEnergyRef.current;
        const glow = Math.min(1, e * 0.6);
        const borderGlow = 0.08 + glow * 0.25;
        authCard.style.borderColor = `rgba(0,0,0,${borderGlow})`;
        authCard.style.boxShadow = `0 12px 48px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,${0.02 + glow * 0.06}), 0 0 ${10 + glow * 20}px rgba(0,0,0,${glow * 0.05})`;
      }

      // ── Decay field (trail fade) ───────────────────────────────────────
      for (let i = 0; i < field.length; i++) {
        field[i] *= DECAY;
      }

      // ── Update particles ──────────────────────────────────────────────
      for (let i = 0; i < NP; i++) {
        // Flow field drives velocity
        const angle = flowAngle(px[i], py[i], time);
        const speed = 0.4 + typingEnergyRef.current * 2.8;
        pvx[i] = pvx[i] * 0.92 + Math.cos(angle) * speed * 0.08;
        pvy[i] = pvy[i] * 0.92 + Math.sin(angle) * speed * 0.08;

        px[i] += pvx[i];
        py[i] += pvy[i];
        plife[i] -= 1;

        // Respawn if dead or out of bounds
        if (plife[i] <= 0 || px[i] < 0 || px[i] >= FW || py[i] < 0 || py[i] >= FH) {
          // Respawn from edge or random
          if (Math.random() < 0.3) {
            // Edge respawn
            const edge = Math.floor(Math.random() * 4);
            if (edge === 0) { px[i] = Math.random() * FW; py[i] = 0; }
            else if (edge === 1) { px[i] = Math.random() * FW; py[i] = FH - 1; }
            else if (edge === 2) { px[i] = 0; py[i] = Math.random() * FH; }
            else { px[i] = FW - 1; py[i] = Math.random() * FH; }
          } else {
            px[i] = Math.random() * FW;
            py[i] = Math.random() * FH;
          }
          pvx[i] = 0;
          pvy[i] = 0;
          plife[i] = 80 + Math.random() * 120;
        }

        // ── Deposit into field ─────────────────────────────────────────
        const ix = Math.floor(px[i]);
        const iy = Math.floor(py[i]);
        if (ix >= 0 && ix < FW && iy >= 0 && iy < FH) {
          const idx = iy * FW + ix;
          field[idx] = Math.min(1, field[idx] + 0.05);
          // Also deposit to neighbors for smoother trails
          if (ix + 1 < FW) field[idx + 1] = Math.min(1, field[idx + 1] + 0.025);
          if (ix - 1 >= 0) field[idx - 1] = Math.min(1, field[idx - 1] + 0.025);
          if (iy + 1 < FH) field[idx + FW] = Math.min(1, field[idx + FW] + 0.025);
          if (iy - 1 >= 0) field[idx - FW] = Math.min(1, field[idx - FW] + 0.025);
        }
      }

      // ── Threshold the field — INVERTED: ink on paper ───────────────────
      // field[i] ranges 0..1. High density → black (ink), low → white (paper).
      // The soft knee creates organic edges — like a Xerox on white paper.
      const THRESH = 0.28;
      const KNEE = 0.06;
      for (let i = 0; i < FW * FH; i++) {
        const v = field[i];
        let val: number;
        if (v < THRESH - KNEE) val = 255;   // paper (white)
        else if (v > THRESH + KNEE) val = 0;  // ink (black)
        else {
          val = 255 - ((v - (THRESH - KNEE)) / (KNEE * 2)) * 255;
        }
        const di = i * 4;
        data[di] = val;
        data[di + 1] = val;
        data[di + 2] = val;
        data[di + 3] = 255;
      }
      octx.putImageData(imgData, 0, 0);

      // ── Upscale to fullscreen with smoothing ──────────────────────────
      const cw = canvas.width;
      const ch = canvas.height;
      ctx.clearRect(0, 0, cw, ch);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(off, 0, 0, FW, FH, 0, 0, cw, ch);

      // ── Very subtle scan lines — ink texture on paper ─────────────────
      const dpr = window.devicePixelRatio;
      ctx.save();
      ctx.globalAlpha = 0.02;
      ctx.fillStyle = "#000";
      for (let y = 0; y < ch; y += 3 * dpr) {
        ctx.fillRect(0, y, cw, 1 * dpr);
      }
      ctx.restore();

      raf = requestAnimationFrame(animate);
    }
    animate();

    function resize() {
      const cv = canvas; cv.width = cv.offsetWidth * window.devicePixelRatio;
      cv.height = cv.offsetHeight * window.devicePixelRatio;
    }
    resize();
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseout", onMouseLeave);
      delete (window as any).__spawnKeyArt;
    };
  }, []);

  // ── Fact cycling ──────────────────────────────────────────────────────
  useEffect(() => {
    if (facts.length === 0) return;
    const interval = setInterval(() => {
      setFactIndex((i) => (i + 1) % facts.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [facts]);

  // ── Boot sequence ─────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    async function boot() {
      setBootState("checking");
      const h = await checkHealth();
      if (!mounted) return;
      setHealth(h);
      setFacts(getAllFacts(h));

      const urlParams = new URLSearchParams(window.location.search);
      const magicToken = urlParams.get("token");
      if (magicToken) {
        setBootState("auth");
        const result = await verifyMagicLink(magicToken);
        if (!mounted) return;
        if (result.ok && result.jwt) {
          setBootState("ready");
          setTimeout(() => {
            if (mounted) onReady(result.jwt!, result.tenant_id ?? null, result.role || "client");
          }, 800);
          return;
        }
        setError(result.error || "Invalid magic link");
        setBootState("holding");
        return;
      }

      const existingJwt = getJWT();
      if (existingJwt) {
        setBootState("ready");
        setTimeout(() => {
          if (mounted) onReady(existingJwt, null, "client");
        }, 800);
        return;
      }

      setBootState("auth");
      const bridge = await bridgeSession();
      if (!mounted) return;
      if (bridge.ok && bridge.jwt) {
        setBootState("ready");
        setTimeout(() => {
          if (mounted) onReady(bridge.jwt!, bridge.tenant_id ?? null, bridge.role || "client");
        }, 800);
        return;
      }
      setBootState("holding");
    }
    boot();
    return () => { mounted = false; };
  }, [onReady]);

  const handleMagicLink = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      const result = await requestMagicLink(email);
      if (result.ok) {
        setSent(true);
        if (result.dev_token) setDevToken(result.dev_token);
      } else {
        setError(result.error || "Failed to send magic link");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [email, busy]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if ((window as any).__spawnKeyArt) (window as any).__spawnKeyArt();
  }, []);

  const stateLabel: Record<BootState, string> = {
    checking: "Checking platform…",
    auth: "Authenticating…",
    holding: "Sign in",
    ready: "Ready",
    app: "Loading workspace…",
    error: "Error",
  };

  return (
    <div className="loading-screen">
      <canvas ref={canvasRef} className="ripple-canvas" />
      <div className="loading-content">
        <div className="loading-logo">
          <div className="logo-mark">R</div>
          <span className="logo-text">GetRida</span>
        </div>

        {bootState !== "holding" && (
          <div className="fact-display">
            <p key={factIndex} className="fact-text">{facts[factIndex]}</p>
          </div>
        )}

        {bootState !== "holding" && bootState !== "error" && (
          <div className="boot-status">
            <div className="boot-spinner" />
            <span className="boot-label">{stateLabel[bootState]}</span>
          </div>
        )}

        {bootState === "holding" && (
          <div className="auth-card">
            <h2 className="auth-title">{sent ? "Check your email" : "Welcome"}</h2>
            {sent ? (
              <>
                <p className="auth-msg">
                  We sent a secure sign-in link to <strong>{email}</strong>.
                  Open it on this device to continue.
                </p>
                {devToken && (
                  <a
                    href={`?token=${devToken}`}
                    className="auth-btn dev-signin-link"
                    style={{ display: "block", textAlign: "center", textDecoration: "none", marginTop: "12px" }}
                  >
                    ⟶ Sign in now (dev mode)
                  </a>
                )}
              </>
            ) : (
              <>
                <p className="auth-msg">Enter your email to access your workspace.</p>
                <form onSubmit={handleMagicLink} className="auth-form">
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="you@business.com"
                    value={email}
                    onChange={handleEmailChange}
                    className="auth-input"
                  />
                  <button type="submit" disabled={busy} className="auth-btn">
                    {busy ? "Sending…" : "Send sign-in link"}
                  </button>
                </form>
              </>
            )}
            {error && <p className="auth-error">{error}</p>}
          </div>
        )}

        {bootState === "error" && (
          <div className="auth-card">
            <h2 className="auth-title">Connection error</h2>
            <p className="auth-error">{error}</p>
            <button className="auth-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
